import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  Wallet,
} from "lucide-react";
import {
  useGetWithdrawRequestsQuery,
  useUpdateWithdrawRequestStatusMutation,
} from "../../state/api";

const STATUS_OPTIONS = ["PENDING", "APPROVED", "REJECTED", "DONE"];
const ALLOWED_ROW_STATUS = new Set(STATUS_OPTIONS);

/** Maps legacy COMPLETED to DONE so the row select matches an option. */
function withdrawStatusSelectValue(status) {
  if (!status) return "PENDING";
  const u = String(status).toUpperCase();
  const mapped = u === "COMPLETED" ? "DONE" : u;
  return ALLOWED_ROW_STATUS.has(mapped) ? mapped : "PENDING";
}

const WithdrawRequests = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState("ALL");

  const { data, isLoading, error, refetch } = useGetWithdrawRequestsQuery({
    page: currentPage,
    limit: itemsPerPage,
    paymentStatus: statusFilter === "ALL" ? undefined : statusFilter,
  });

  const [updateStatus, { isLoading: isUpdating }] =
    useUpdateWithdrawRequestStatusMutation();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleString();
    } catch {
      return String(d);
    }
  };

  const displayName = (user) => {
    if (!user) return "—";
    const n = [user.name, user.lastName].filter(Boolean).join(" ").trim();
    return n || user.phone || user.email || "—";
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Wallet className="w-8 h-8" />
              Withdraw requests
            </h1>
            <p className="text-muted-foreground mt-1">
              Sorted by latest request date. Update status per row (no deletes).
            </p>
          </div>
          <Button variant="outline" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle>Requests</CardTitle>
              <CardDescription>
                {total} total · Page {currentPage} of {Math.max(totalPages, 1)}
              </CardDescription>
            </div>
            <div className="w-[180px]">
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All statuses</SelectItem>
                  <SelectItem value="PENDING">PENDING</SelectItem>
                  <SelectItem value="APPROVED">APPROVED</SelectItem>
                  <SelectItem value="REJECTED">REJECTED</SelectItem>
                  {/* <SelectItem value="COMPLETED">COMPLETED</SelectItem> */}
                  <SelectItem value="DONE">DONE</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading && (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            )}
            {error && (
              <p className="text-destructive text-center py-8">
                Failed to load withdraw requests. Check admin token and API.
              </p>
            )}
            {!isLoading && !error && (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>UPI ID</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Fee</TableHead>
                      <TableHead>Net payout</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>User ID</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8">
                          No withdraw requests found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((row) => (
                        <TableRow key={row._id}>
                          <TableCell className="font-medium">
                            {displayName(row.user)}
                          </TableCell>
                          <TableCell>
                            {row.user?.phone ?? "—"}
                          </TableCell>
                          <TableCell>
                            <div className="font-mono text-sm max-w-[200px] truncate" title={row.user?.upiId}>
                              {row.user?.upiId ?? "—"}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              UPI edits: {row.user?.upiIdUpdateCount ?? 0}
                            </div>
                          </TableCell>
                          <TableCell>₹{row.amount ?? "—"}</TableCell>
                          <TableCell>
                            {row.feeAmount != null ? (
                              <div>
                                <div>₹{row.feeAmount}</div>
                                <div className="text-xs text-muted-foreground">
                                  {row.feePercent != null
                                    ? `${row.feePercent}%`
                                    : ""}
                                </div>
                              </div>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>
                            {row.netAmount != null ? `₹${row.netAmount}` : "—"}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={withdrawStatusSelectValue(row.paymentStatus)}
                              disabled={isUpdating}
                              onValueChange={(v) =>
                                updateStatus({
                                  id: row._id,
                                  paymentStatus: v,
                                })
                              }
                            >
                              <SelectTrigger className="h-8 w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map((s) => (
                                  <SelectItem key={s} value={s}>
                                    {s}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="font-mono text-xs max-w-[140px] truncate">
                            {row.userId}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {formatDate(row.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <div className="flex items-center justify-end gap-2 mt-6">
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage(1)}
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm px-2">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={currentPage >= totalPages}
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WithdrawRequests;
